/**
 * Font Library cascade resolution — frontend mirror of
 * backend/tend_service/services/font_resolution.py. Keep the resolution
 * rule identical:
 *
 *   slug = config[element + 'FontFamily'] ?? manifest.defaults[element].slug
 *   → look up in manifest.families; unknown/deleted slugs fall through;
 *   no match → the bare Inter fallback stack.
 *
 * Cascade: universal → brand → template → content → slide (most specific
 * wins). Template/content/slide layers are sparse `*FontFamily` keys in
 * overlay_config blobs; the universal/brand base layer arrives pre-resolved
 * in the manifest's `defaults`.
 */

import type { OverlayTemplateConfig } from '../api/tendApi';

// Must equal the FONT_FAMILY constants in the Remotion compositions and the
// Inter stack in overlay.html — the terminal fallback everywhere.
export const FONT_FALLBACK_STACK =
  'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

export type FontElement = 'title' | 'subheader' | 'body' | 'category';

export const FONT_ELEMENTS: FontElement[] = ['title', 'subheader', 'body', 'category'];

/** UI labels — 'title' displays as "Header" per product naming. */
export const FONT_ELEMENT_LABELS: Record<FontElement, string> = {
  title: 'Header',
  subheader: 'Subheader',
  body: 'Body',
  category: 'Category',
};

export interface FontManifestVariant {
  weight: number;
  style: 'normal' | 'italic';
  format: string;
  url: string;
}

export interface FontManifestFamily {
  id: number;
  slug: string;
  name: string;
  cssFamily: string;
  scope: 'universal' | 'brand';
  source: 'google' | 'custom';
  category: string | null;
  fallbackStack: string;
  variants: FontManifestVariant[];
}

export interface FontManifestDefault {
  familyId: number;
  slug: string;
  cssFamily: string;
  source: 'brand' | 'universal';
}

export interface FontManifest {
  brand_slug: string | null;
  families: FontManifestFamily[];
  defaults: Record<FontElement, FontManifestDefault | null>;
}

const configKey = (element: FontElement): keyof OverlayTemplateConfig =>
  `${element}FontFamily` as keyof OverlayTemplateConfig;

/** Resolve one element's effective family slug ('' = Inter fallback). */
export function resolveElementFontSlug(
  config: Partial<OverlayTemplateConfig> | null | undefined,
  element: FontElement,
  manifest: FontManifest | null | undefined,
): string {
  if (!manifest) return '';
  const bySlug = new Map(manifest.families.map((f) => [f.slug, f]));

  const configSlug = config?.[configKey(element)] as string | undefined;
  if (configSlug && bySlug.has(configSlug)) return configSlug;

  const def = manifest.defaults?.[element];
  if (def && bySlug.has(def.slug)) return def.slug;

  return '';
}

/** Resolve one element's CSS font-family stack (always ends in Inter stack). */
export function resolveElementFontFamily(
  config: Partial<OverlayTemplateConfig> | null | undefined,
  element: FontElement,
  manifest: FontManifest | null | undefined,
): string {
  const slug = resolveElementFontSlug(config, element, manifest);
  if (!slug) return FONT_FALLBACK_STACK;
  const family = manifest!.families.find((f) => f.slug === slug)!;
  return `"${family.cssFamily}", ${FONT_FALLBACK_STACK}`;
}

export interface InheritedFontInfo {
  slug: string | null;      // effective inherited slug (null = Inter)
  name: string;             // display name ("Inter" when falling back)
  source: 'template' | 'brand' | 'universal' | 'default';
}

/**
 * What an element would inherit if its own config key were unset — used for
 * the picker's pinned "Inherit — {name} ({source})" row.
 *
 * `templateConfig` is the active template's config when editing CONTENT
 * (content inherits from template before brand); pass null when editing a
 * template itself (templates inherit straight from brand/universal).
 */
export function resolveInheritedFont(
  templateConfig: Partial<OverlayTemplateConfig> | null | undefined,
  element: FontElement,
  manifest: FontManifest | null | undefined,
): InheritedFontInfo {
  if (manifest) {
    const bySlug = new Map(manifest.families.map((f) => [f.slug, f]));
    const templateSlug = templateConfig?.[configKey(element)] as string | undefined;
    if (templateSlug && bySlug.has(templateSlug)) {
      return { slug: templateSlug, name: bySlug.get(templateSlug)!.name, source: 'template' };
    }
    const def = manifest.defaults?.[element];
    if (def && bySlug.has(def.slug)) {
      return { slug: def.slug, name: bySlug.get(def.slug)!.name, source: def.source };
    }
  }
  return { slug: null, name: 'Inter', source: 'default' };
}
